"use client";

import { isFilled } from "@prismicio/client";
import { CohortCardData } from "@up/cart";
import {
  APPLY_NOW_URL,
  filterCohorts,
  generateCourseFilter,
  generateDurationFilter,
  generateStartDateFilter,
  handleCohortFilterChange,
  hrefResolver,
  resetCohortFilters,
} from "@up/data";
import { BrowserOnly, CohortFilters } from "@up/ui";
import * as PropTypes from "prop-types";
import { useRef, useState } from "react";

import CohortCard from "~/components/CohortCard";

const C = function ({ cohort, slice, isCoursePage }) {
  const URL = (() => {
    if (slice.primary.buy_now) {
      return `/express/${cohort._id}`;
    }

    if (isFilled.link(slice.primary.cta_url_override)) {
      return hrefResolver(slice.primary.cta_url_override);
    }

    return APPLY_NOW_URL;
  })();

  return (
    <CohortCardData
      cohort={cohort}
      deadlineKey={slice.primary.deadline_key || "app_deadline"}
      isCoursePage={isCoursePage}
      url={URL}
    >
      <CohortCard
        buttonClasses="button button-medium font-white"
        buttonText={slice.primary.cta_button_text}
        showItemType={true}
      />
    </CohortCardData>
  );
};

const CardsInGrid = function ({ cohorts, slice, isCoursePage }) {
  return (
    <BrowserOnly>
      <div
        className={`gap-10 grid grid-cols-1 ${
          cohorts.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3"
        }`}
      >
        {cohorts.map((cohort) => {
          return (
            <C
              key={cohort._id}
              cohort={cohort}
              slice={slice}
              isCoursePage={isCoursePage}
            />
          );
        })}
      </div>
    </BrowserOnly>
  );
};

const CohortListing = function ({ cohorts, slice, isCoursePage = false }) {
  const [head1, head2, head3, ...tail] = cohorts;
  const head = [head1, head2, head3].filter((x) => x !== undefined);

  const showAllButton = useRef(null);

  const [showAllCohorts, setShowAllCohorts] = useState(false);
  const [filters, setFilters] = useState(() => {
    const enabledFilters = {
      startDate: true,
      duration: [...new Set(cohorts.map((c) => c.cohortLength))].length > 1,
      course: [...new Set(cohorts.map((c) => c.catalogueItem.name))].length > 1,
    };

    return [
      generateStartDateFilter({ enabledFilters, inventoryItems: cohorts }),
      generateDurationFilter({ enabledFilters, inventoryItems: cohorts }),
      generateCourseFilter({ enabledFilters, inventoryItems: cohorts }),
    ];
  });

  const toggleShowAll = function () {
    const showAll = !showAllCohorts;
    setShowAllCohorts(showAll);

    if (showAll) {
      if (!showAllButton.current) {
        return;
      }

      setTimeout(function () {
        const $button = showAllButton.current;
        $button.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  };

  const resetFilters = function () {
    setFilters(resetCohortFilters(filters));
  };

  const handleFilterToggle = function (filterName, optionName) {
    setFilters(handleCohortFilterChange(filters, filterName, optionName));
  };

  const filteredCohorts = filterCohorts(cohorts, filters);

  const resetButtonClass = " button button-small";

  const { limit_visibility_of_options: limitVisibility } = slice.primary;

  return (
    <div id="section-courses" className="cohort-listing">
      {!limitVisibility && (
        <CardsInGrid
          cohorts={cohorts}
          slice={slice}
          isCoursePage={isCoursePage}
        />
      )}

      {limitVisibility && (
        <>
          <CardsInGrid
            cohorts={head}
            slice={slice}
            isCoursePage={isCoursePage}
          />
          {Boolean(tail.length) && (
            <button
              className={`expander text-left ${showAllCohorts ? "active" : ""}`}
              onClick={toggleShowAll}
              ref={showAllButton}
            >
              {showAllCohorts ? "Show Fewer Options" : "Show More Options"}
            </button>
          )}

          {showAllCohorts && Boolean(tail.length) && (
            <div className="animation-fade-in cohort-listing-filters relative">
              <div className="cohort-listing-filters-window animation-fade-in z-60">
                <CohortFilters
                  filters={filters}
                  handleFilterToggle={handleFilterToggle}
                />
              </div>
            </div>
          )}

          {showAllCohorts ? (
            filteredCohorts.length ? (
              <CardsInGrid
                cohorts={filteredCohorts}
                slice={slice}
                isCoursePage={isCoursePage}
              />
            ) : (
              <div className="text-center">
                No cohorts match these filters
                <br />
                <br />
                <button className={resetButtonClass} onClick={resetFilters}>
                  Reset Filters
                </button>
              </div>
            )
          ) : null}
        </>
      )}
    </div>
  );
};

CohortListing.propTypes = {
  cohorts: PropTypes.array,
  slice: PropTypes.object,
};

export default CohortListing;
